import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { Router } from "@angular/router";
import { of, throwError, combineLatest } from "rxjs";
import { map } from "rxjs/operators";

/** Services **/
import { AuthenticationService } from "../../core/authentication/authentication.service";

/** Models **/
import { ModulesEnum } from "../models/modules-enum.model";
import { TipoUsuarioEnum } from "../models/tipo-usuario-enum.model";
import { Util } from "../utils/util";
import { TipoEntidadeEnum } from "../models/tipo-entidade-enum";

@Injectable()
export class DropdownListService {
  constructor(
    private authService: AuthenticationService,
    private router: Router
  ) {}

  /**
   * Retorna as entidades acessíveis do usuário logado de acordo com o tipo.
   */
  private getEntidadesAcessiveis(
    module: ModulesEnum,
    permissaoAcao: any[]
  ): Observable<any> {
    try {
      const permAcao = permissaoAcao.filter(
        (pa) => pa.Permissao.IdPermissao === module && pa.Acao.IdAcao === 28
      );
      const entidades = [];

      permAcao.forEach((i) =>
        i.Entidades.forEach((obj) => {
          if (
            !entidades.some(
              (item) =>
                (item.idAutoridadeCertificadora &&
                  obj.Objeto.idAutoridadeCertificadora &&
                  item.idAutoridadeCertificadora ===
                    obj.Objeto.idAutoridadeCertificadora) ||
                (item.idAutoridadeRegistro &&
                  obj.Objeto.idAutoridadeRegistro &&
                  item.idAutoridadeRegistro ===
                    obj.Objeto.idAutoridadeRegistro) ||
                (item.idLocalAtendimento &&
                  obj.Objeto.idLocalAtendimento &&
                  item.idLocalAtendimento === obj.Objeto.idLocalAtendimento)
            )
          ) {
            entidades.push(obj.Objeto);
          }
        })
      );

      return of(entidades);
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Gera um dropdownlist com as Autoridades Certificadoras que o usuário tem permissão no módulo em que está acessando.
   */
  public getAutoridadeCertificadoraDropdownList(): Observable<any> {
    try {
      return combineLatest(
        this.authService.IdentityPermissionAction,
        this.authService.IdentityEntitiesPermission,
        this.authService.UserType
      ).pipe(
        map((pipedResults) => {
          const result = {
            permissaoAcao: pipedResults[0],
            entidadePermissao: pipedResults[1],
            userType: pipedResults[2],
          };

          let entidadeList;

          this.getEntidadesAcessiveis(
            Util.getModuleByRoute(this.router),
            result.permissaoAcao
          ).subscribe((entAcessiveis) => {
            // Lógica caso o usuário seja de Tipo Autoridade Certificadora (AC)
            if (result.userType === TipoUsuarioEnum.AutoridadeCertificadora) {
              entidadeList = result.entidadePermissao.filter((ac) => {
                return (
                  ac.Tipo === TipoEntidadeEnum.AutoridadeCertificadora &&
                  entAcessiveis.some(
                    (ent) => ent.idAutoridadeCertificadora === ac.idEntidade
                  )
                );
              });
            }

            // Lógica caso o usuário seja de Tipo Autoridade de Registro (AR)
            else if (result.userType === TipoUsuarioEnum.AutoridadeRegistro) {
              // Obtem a lista de ARs a partir do entidadesPermissão
              const listaAR = result.entidadePermissao.filter(
                (ent) =>
                  ent.Tipo === TipoEntidadeEnum.AutoridadeRegistro &&
                  entAcessiveis.some(
                    (ar) => ent.idEntidade === ar.idAutoridadeRegistro
                  )
              );

              entidadeList = result.entidadePermissao.filter(
                (ac) =>
                  ac.Tipo === TipoEntidadeEnum.AutoridadeCertificadora &&
                  listaAR.some((ar) => ar.idEntidadePai === ac.idEntidade)
              );

              entidadeList = entidadeList.filter((thing, index) => {
                return (
                  index ===
                  entidadeList.findIndex((obj) => {
                    return JSON.stringify(obj) === JSON.stringify(thing);
                  })
                );
              });
            }

            // Lógica caso o usuário seja de Tipo Local de Atendimento (LA)
            else {
              // Obtem a lista de ARs a partir do entidadesPermissão
              const listaAR = result.entidadePermissao.filter((ar) =>
                result.entidadePermissao.some(
                  (la) =>
                    la.idEntidadePai === ar.idEntidade &&
                    la.Tipo === TipoEntidadeEnum.LocalAtendimento &&
                    entAcessiveis.some(
                      (ent) =>
                        ent.idLocalAtendimento === la.idEntidade &&
                        la.Tipo === TipoEntidadeEnum.LocalAtendimento
                    )
                )
              );

              entidadeList = result.entidadePermissao.filter(
                (ac) =>
                  ac.Tipo === TipoEntidadeEnum.AutoridadeCertificadora &&
                  listaAR.some((ar) => ar.idEntidadePai === ac.idEntidade)
              );

              entidadeList = entidadeList.filter((thing, index) => {
                return (
                  index ===
                  entidadeList.findIndex((obj) => {
                    return JSON.stringify(obj) === JSON.stringify(thing);
                  })
                );
              });
            }

            // Monta a lista para exibição correta
            entidadeList = entidadeList.map((ea) => {
              return <any>{
                idAutoridadeCertificadora: ea.idEntidade,
                Nome: ea.Nome,
                idStatus: ea.idStatus,
              };
            });
          });

          return Util.orderBy(entidadeList, "Nome");
        })
      );
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Gera um dropdownlist com os Locais de Atendimento
   * que o usuário tem permissão no módulo em que está acessando.
   * @param idAutoridadeRegistro ID da autoridade de registro.
   */
  public getLocalAtendimentoDropdownListByAR(
    idAutoridadeRegistro: number
  ): Observable<any> {
    try {
      return this.getListLocalAtendimento().pipe(
        map((ret) => {
          let locais = [];
          if (ret && ret.length > 0) {
            locais = ret.filter(
              (la) => la.idEntidadePai === idAutoridadeRegistro
            );
          }
          return Util.orderBy(locais, "Nome");
        })
      );
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Gera um dropdownlist com os Locais de Atendimento com Status diferente de Cancelado e Desabilitado.
   * @param idAutoridadeRegistro ID da autoridade de registro.
   */
  public getLocalAtendimentoDropdownListHabilitados(
    idAutoridadeRegistro: number
  ): Observable<any> {
    try {
      return this.getLocalAtendimentoDropdownListByAR(
        idAutoridadeRegistro
      ).pipe(
        map((ret) => {
          let locais = [];
          if (ret && ret.length > 0) {
            locais = ret.filter((la) => la.idStatus !== 2 && la.idStatus !== 5);
          }
          return Util.orderBy(locais, "Nome");
        })
      );
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Gera um dropdownlist com os Locais de Atendimento do tipo Instalação Técnica.
   * @param idAutoridadeRegistro ID da autoridade de registro.
   */
  public getLocalAtendimentoDropdownListInstalacaoTecnica(
    idAutoridadeRegistro: number
  ): Observable<any> {
    try {
      let locais = [];

      this.getLocalAtendimentoDropdownListByAR(idAutoridadeRegistro).subscribe(
        (ret) => {
          locais = ret.filter((la) => la.LocalAtendimentoTipo === 1);
        }
      );

      return of(Util.orderBy(locais, "Nome"));
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Gera um dropdownlist com as Autoridades de Registro
   * que o usuário tem permissão no módulo em que está acessando a partir de uma AC.
   * @param idAutoridadeCertificadora ID da autoridade certificadora.
   */
  public getAutoridadeRegistroDropdownListByAC(
    idAutoridadeCertificadora: number
  ): Observable<any> {
    try {
      return this.getListAutoridadeRegistro(false).pipe(
        map((ret) => {
          let listaAR = [];
          if (ret && ret.length > 0 && idAutoridadeCertificadora > 0) {
            listaAR = ret.filter(
              (ar) => ar.idEntidadePai === idAutoridadeCertificadora
            );
          }
          return Util.orderBy(listaAR, "Nome");
        })
      );
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Gera um dropdownlist com as Autoridades de Registro
   * que o usuário tem permissão no módulo em que está acessando.
   */
  public getAutoridadeRegistroDropdownList(): Observable<any> {
    try {
      return this.getListAutoridadeRegistro(false).pipe(
        map((val) => Util.orderBy(val, "Nome"))
      );
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Gera um dropdownlist com as Autoridades de Registro
   * que o usuário tem permissão no módulo
   * em que está acessando sem duplicar (caso esteja em outras ACs).
   */
  public getAutoridadeRegistroDropdownListDistinct(): Observable<any> {
    try {
      return this.getListAutoridadeRegistro(true).pipe(
        map((val) => Util.orderBy(val, "Nome"))
      );
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Busca as autoridades de registro
   * que possuam algum local de atendimento vinculado.
   */
  public getAutoridadeRegistroDropdownListComLocal(): Observable<any> {
    try {
      let arLocalList = [];
      const EntidadesPermissao = [];

      this.getAutoridadeRegistroDropdownList().subscribe((arList) => {
        arLocalList = arList.filter((ar) =>
          EntidadesPermissao.some(
            (ep) =>
              ep.idEntidadePai === ar.idAutoridadeRegistro && ep.Tipo === 4
          )
        );
      });

      return of(Util.orderBy(arLocalList, "Nome"));
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Retorna uma lista com id e Nome das Autoridades de Registro
   * que o usuário tem permissão no módulo em que está acessando.
   */
  private getListAutoridadeRegistro(
    removeDuplicates: boolean
  ): Observable<any> {
    try {
      return combineLatest(
        this.authService.IdentityPermissionAction,
        this.authService.IdentityEntitiesPermission,
        this.authService.UserType
      ).pipe(
        map((pipedResults) => {
          const result = {
            permissaoAcao: pipedResults[0],
            entidadePermissao: pipedResults[1],
            userType: pipedResults[2],
          };
          let entidadeList = [];

          this.getEntidadesAcessiveis(
            Util.getModuleByRoute(this.router),
            result.permissaoAcao
          ).subscribe((entAcessiveis) => {
            // Lógica caso o usuário seja de Tipo (AC)
            if (result.userType === TipoUsuarioEnum.AutoridadeCertificadora) {
              entidadeList = result.entidadePermissao.filter((ar) =>
                entAcessiveis.some(
                  (ent) =>
                    ent.idAutoridadeCertificadora === ar.idEntidadePai &&
                    ar.Tipo === TipoEntidadeEnum.AutoridadeRegistro
                )
              );
            }

            //Lógica caso o usuário seja de Tipo (AR)
            else if (result.userType === TipoUsuarioEnum.AutoridadeRegistro) {
              entidadeList = result.entidadePermissao.filter((ar) =>
                entAcessiveis.some(
                  (ent) =>
                    ent.idAutoridadeRegistro === ar.idEntidade &&
                    ar.Tipo === TipoEntidadeEnum.AutoridadeRegistro
                )
              );
            }

            //Lógica caso o usuário seja de Tipo (LA)
            else {
              entidadeList = result.entidadePermissao.filter((ar) =>
                result.entidadePermissao.some(
                  (la) =>
                    la.idEntidadePai === ar.idEntidade &&
                    la.Tipo === TipoEntidadeEnum.LocalAtendimento &&
                    entAcessiveis.some(
                      (ent) =>
                        ent.idLocalAtendimento === la.idEntidade &&
                        la.Tipo === TipoEntidadeEnum.LocalAtendimento
                    )
                )
              );
            }

            // Remove ARs duplicadas que estão em várias ACs
            if (removeDuplicates) {
              // Monta a lista para exibição correta
              entidadeList = entidadeList.map((ar) => {
                return <any>{
                  idAutoridadeRegistro: ar.idEntidade,
                  Nome: ar.Nome,
                  idStatus: ar.idStatus,
                  idEntidadePai: ar.idEntidadePai,
                };
              });

              entidadeList = Array.from(
                new Set(entidadeList.map((a) => a.idAutoridadeRegistro))
              ).map((id) => {
                return entidadeList.find((a) => a.idAutoridadeRegistro === id);
              });
            } else {
              // Monta a lista para exibição correta e valida se necessita colocar o nome da AC (caso de multiplas ACs)
              entidadeList = entidadeList.map((ar) => {
                var nomeAc =
                  entidadeList.filter(
                    (ent) => ent.idEntidadePai === ar.idEntidadePai
                  ).length === 1
                    ? ar.Nome
                    : ar.Nome +
                      `${
                        result.userType === TipoUsuarioEnum.LocalAtendimento
                          ? ""
                          : " (" +
                            this.getEntidadeNome(
                              ar.idEntidadePai,
                              TipoEntidadeEnum.AutoridadeCertificadora,
                              result.entidadePermissao
                            ) +
                            ")"
                      }`;

                return {
                  idAutoridadeRegistro: ar.idEntidade,
                  idStatus: ar.idStatus,
                  idEntidadePai: ar.idEntidadePai,
                  Nome: nomeAc,
                };
              });
            }
          });

          return entidadeList;
        })
      );
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Retorna uma lista com id e Nome dos Locais de Atendimento que o usuário tem permissão no módulo em que está acessando.
   */
  private getListLocalAtendimento(): Observable<any> {
    try {
      return combineLatest(
        this.authService.IdentityPermissionAction,
        this.authService.IdentityEntitiesPermission,
        this.authService.UserType
      ).pipe(
        map((pipedResults) => {
          const result = {
            permissaoAcao: pipedResults[0],
            entidadePermissao: pipedResults[1],
            userType: pipedResults[2],
          };
          let entidadeList = [];

          this.getEntidadesAcessiveis(
            Util.getModuleByRoute(this.router),
            result.permissaoAcao
          ).subscribe((entAcessiveis) => {
            // Lógica caso o usuário seja de Tipo Autoridade Certificadora (AC)
            if (result.userType === TipoUsuarioEnum.AutoridadeCertificadora) {
              entidadeList = result.entidadePermissao.filter((la) => {
                return (
                  la.Tipo === TipoEntidadeEnum.LocalAtendimento &&
                  result.entidadePermissao.some(
                    (ar) =>
                      ar.idEntidade === la.idEntidadePai &&
                      result.entidadePermissao.some(
                        (ac) =>
                          ac.idEntidade === ar.idEntidadePai &&
                          entAcessiveis.some(
                            (ent) =>
                              ent.idAutoridadeCertificadora === ac.idEntidade
                          )
                      )
                  )
                );
              });
            }

            // Lógica caso o usuário seja de Tipo Autoridade de Registro (AR)
            else if (result.userType === TipoUsuarioEnum.AutoridadeRegistro) {
              entidadeList = result.entidadePermissao.filter((la) => {
                return (
                  la.Tipo === TipoEntidadeEnum.LocalAtendimento &&
                  result.entidadePermissao.some(
                    (ar) =>
                      ar.idEntidade === la.idEntidadePai &&
                      entAcessiveis.some(
                        (ent) => ent.idAutoridadeRegistro === ar.idEntidade
                      )
                  )
                );
              });
            }

            // Lógica caso o usuário seja de Tipo Local de Atendimento (LA)
            else {
              entidadeList = result.entidadePermissao.filter((la) => {
                return entAcessiveis.some(
                  (ent) =>
                    ent.idLocalAtendimento === la.idEntidade &&
                    la.Tipo === TipoEntidadeEnum.LocalAtendimento
                );
              });
            }

            // Monta a lista para exibição correta
            entidadeList = entidadeList.map((la) => {
              return <any>{
                idLocalAtendimento: la.idEntidade,
                idEntidadePai: la.idEntidadePai,
                Nome: la.Nome,
                idStatus: la.idStatus,
                LocalAtendimentoTipo: la.LocalAtendimentoTipo,
              };
            });
          });

          return entidadeList;
        })
      );
    } catch (error) {
      return throwError(new Error(error));
    }
  }

  /**
   * Retorna o nome da entidade de acordo com os parâmetros informados.
   * @param idEntidade id da entidade que deseja saber o nome.
   * @param idTipo Tipo da entidade.
   * @param entidadePermissao Lista de EntidadePermissao do usuário autenticado.
   */
  public getEntidadeNome(
    idEntidade: number,
    idTipo: TipoEntidadeEnum,
    entidadePermissao: any[]
  ) {
    return entidadePermissao.find(
      (ep) => ep.idEntidade === idEntidade && ep.Tipo === idTipo
    ).Nome;
  }
}
